/**
 * The customer card's "Invoices" tab.
 *
 * Two parts, no status filter:
 *  - Drafts: a flat working list above the tree, shown only while some exist.
 *    Never collapsed, never summed into the tree, never selectable — a draft
 *    is not yet money owed (`_debt` is 0), and it is easy to keep working on
 *    (edit) or discard (delete, which frees its order for a new draft).
 *  - Issued invoices: everything else (Finvalda rows with no status, and
 *    confirmed ERP rows), rendered as the year/month tree (invoices-tree.tsx)
 *    via `Invoices` in `groupByPeriod` mode.
 *
 * The list is fetched here rather than trusting `customer.invoices` verbatim,
 * because the customer fetch also has to serve the tab's unread-drafts badge
 * and other screens — this component alone knows it needs "all" statuses,
 * with drafts split out before anything reaches the tree.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { Badge } from "catalyst-ui-kit/badge";
import { Invoice } from "types/Invoice";
import Currency from "components/format/currency/currency";
import PrettyDate from "components/format/PrettyDate";
import { API_URL } from "constants.js";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import { fetchCustomerInvoices } from "services/invoices.service";
import { formatPeriodMonth } from "features/billing-period/billing-period";
import { btnIconCls, btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import Invoices from "./Invoices";

type Props = {
  customerId?: string;
  /**
   * The customer's own invoices list, as the customer card already holds it.
   * Not rendered directly — only its identity is watched, so that a refetch
   * of the customer (the container's `onDone` after create/confirm) reloads
   * this tab's own list too.
   */
  invoices?: Invoice[];
  onCreate?: () => void;
  onOpenInvoice?: (invoice: Invoice) => void;
};

const CustomerInvoices = ({
  customerId,
  invoices: customerInvoicesProp,
  onCreate,
  onOpenInvoice,
}: Props) => {
  const { companyId } = useParams<{ companyId: string }>();
  const [list, setList] = useState<Invoice[]>([]);
  // Starts as loading when a fetch is about to run: an initial `false` painted
  // an empty drafts list and tree for a customer who does have invoices.
  const [loading, setLoading] = useState(Boolean(companyId && customerId));
  const [loadFailed, setLoadFailed] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Only the latest request may write: a slower, older response (e.g. from a
  // customer switched away from) must not overwrite what the user now sees.
  const requestSeq = useRef(0);

  const loadInvoices = useCallback(async () => {
    if (!companyId || !customerId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const data = await fetchCustomerInvoices({
        companyId,
        customerId,
        status: "all",
      });
      if (seq !== requestSeq.current) return;
      setList(data.invoices || []);
      setLoadFailed(false);
    } catch {
      if (seq !== requestSeq.current) return;
      // Not an empty list: showing "no invoices" here would hide existing
      // drafts and invite a duplicate. The error replaces the list instead.
      setList([]);
      setLoadFailed(true);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [companyId, customerId]);

  useEffect(() => {
    loadInvoices();
    // `customerInvoicesProp` is a trigger, not a source of data: its identity
    // changes when the container refetches the customer, and that is the only
    // thing about it this effect cares about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, customerId, customerInvoicesProp, loadInvoices]);

  const drafts = list.filter((invoice) => invoice.status === "draft");
  const issued = list.filter((invoice) => invoice.status !== "draft");

  const getDownloadUrl = (fileId: string) =>
    `${API_URL}/${companyId}/files/download/${fileId}`;

  const handleViewPdf = (fileId: string) => {
    if (!fileId || !companyId) return;
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
  };

  /**
   * Deleting a draft releases its order back into the "Create invoice" list.
   * The confirmation is a modalX component, shared with the Outgoing invoices
   * drafts panel.
   */
  const confirmDeleteDraft = (invoice: Invoice) => {
    dispatch(
      setModalxOptions({
        title: t("delete_draft_invoice", {
          defaultValue: "Delete draft invoice",
        }),
        size: "small",
      })
    );
    dispatch(setModalxComponent("DeleteDraftInvoiceDialog"));
    dispatch(
      setModalxComponentProps({
        invoiceId: invoice.id,
        customerName: invoice.customerName,
        onDone: loadInvoices,
      })
    );
  };

  const toolbar = onCreate && (
    <div className="mb-3 flex justify-end">
      <button type="button" className={btnPrimaryCls} onClick={onCreate}>
        {t("create_invoice")}
      </button>
    </div>
  );

  return (
    <div>
      {toolbar}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2
            className="h-5 w-5 animate-spin text-zinc-400"
            aria-hidden="true"
          />
        </div>
      ) : loadFailed ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800/40 dark:bg-red-950/40 dark:text-red-300">
          <span>
            {t("customer_invoices_load_failed", {
              defaultValue: "Could not load invoices.",
            })}
          </span>
          <button
            type="button"
            className={btnSecondaryCls}
            onClick={loadInvoices}
          >
            {t("try_again")}
          </button>
        </div>
      ) : (
        <>
          {drafts.length > 0 && (
            <div className="mb-4">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">
                {t("customer_invoices_drafts_heading", {
                  count: drafts.length,
                  defaultValue: "Drafts ({{count}})",
                })}
              </h3>
              <div className="divide-y divide-[#E6E1DB] rounded-sm border border-[#E6E1DB] bg-white">
                {drafts.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <Badge color="amber">
                        {t("draft", { defaultValue: "Draft" })}
                      </Badge>
                      {invoice.contractRef ? (
                        <span className="text-sm text-gray-700">
                          {invoice.contractRef}
                        </span>
                      ) : null}
                      {invoice.periodFrom ? (
                        <span className="text-xs text-gray-500">
                          {formatPeriodMonth(invoice.periodFrom)}
                        </span>
                      ) : null}
                      <PrettyDate dateString={invoice.date} />
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-900">
                        <Currency
                          value={invoice._amountWithVat}
                          isSymbol
                          isDecimal
                        />
                      </span>
                      <span className="text-sm text-gray-500">
                        <Currency
                          value={invoice._amount}
                          isSymbol
                          isDecimal
                        />
                      </span>
                      <div className="flex items-center gap-1">
                        {onOpenInvoice && (
                          <button
                            type="button"
                            title={t("edit")}
                            className={`${btnIconCls} max-md:size-11`}
                            onClick={() => onOpenInvoice(invoice)}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          title={t("delete", { defaultValue: "Delete" })}
                          className={`${btnIconCls} max-md:size-11`}
                          onClick={() => confirmDeleteDraft(invoice)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Invoices
            invoices={issued}
            groupByPeriod
            onOpenInvoice={onOpenInvoice}
            onViewPdf={handleViewPdf}
            getDownloadUrl={getDownloadUrl}
          />
        </>
      )}
    </div>
  );
};

export default CustomerInvoices;
